"use client"

import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { httpJson } from "@/lib/http"
import { CRM_API_BASE } from "@/lib/config"
import { fetchClientDetail } from "@/lib/client-detail-cache"

interface PortalUser {
  id: number
  email?: string
  name_first?: string
  name_last?: string
  first_name?: string
  last_name?: string
}

interface UserFilterDropdownProps {
  value: string
  onChange: (userId: string, displayName: string | null) => void
  disabled?: boolean
  variant?: "default" | "compact"
  widthClass?: string
  /** Required: scope users to the selected client. Empty/null disables the dropdown. */
  clientId?: string | null
}

const getUserDisplayName = (u: PortalUser): string => {
  const first = u.name_first ?? u.first_name ?? ""
  const last = u.name_last ?? u.last_name ?? ""
  const fullName = `${first} ${last}`.trim()
  if (fullName && u.email) return `${fullName} (${u.email})`
  if (fullName) return fullName
  return u.email ?? `User #${u.id}`
}

export function UserFilterDropdown({
  value,
  onChange,
  disabled = false,
  variant = "default",
  widthClass,
  clientId,
}: UserFilterDropdownProps) {
  const [users, setUsers] = useState<PortalUser[]>([])
  const [allUsers, setAllUsers] = useState<PortalUser[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resolvedSelectedName, setResolvedSelectedName] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const isCompact = variant === "compact"
  const selectedId = value === "all" ? "" : value
  const isClientScoped = clientId != null && clientId !== "" && clientId !== "all"
  const hasNoUsersForClient = isClientScoped && !loading && allUsers.length === 0
  const isEffectivelyDisabled = disabled || !isClientScoped || hasNoUsersForClient

  const inputClass = isCompact
    ? "h-7 text-xs border-gray-300 bg-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary pr-8"
    : "h-10 border border-neutral-300 bg-background focus:border-primary pr-8"

  const containerWidth = widthClass ?? (isCompact ? "w-52" : "w-56")

  const fetchClientUsers = async (cid: string, controller?: AbortController) => {
    try {
      setLoading(true)
      const response = await fetchClientDetail(cid)
      const list = Array.isArray(response.users) ? (response.users as PortalUser[]) : []
      setAllUsers(list)
      setUsers(list)
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) return
      console.error("Error fetching client users:", error)
      setAllUsers([])
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort()
    if (isClientScoped) {
      const controller = new AbortController()
      abortRef.current = controller
      fetchClientUsers(clientId as string, controller)
      return () => controller.abort()
    }
    setAllUsers([])
    setUsers([])
    return
  }, [clientId])

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      setUsers(allUsers)
    } else {
      setUsers(
        allUsers.filter((u) => {
          const haystack = `${getUserDisplayName(u)} ${u.email ?? ""}`.toLowerCase()
          return haystack.includes(term)
        }),
      )
    }
  }, [searchTerm, allUsers])

  useEffect(() => {
    if (!selectedId) {
      setResolvedSelectedName(null)
      return
    }
    const found = users.find((u) => u.id.toString() === selectedId) || allUsers.find((u) => u.id.toString() === selectedId)
    if (found) {
      setResolvedSelectedName(getUserDisplayName(found))
      return
    }
    let cancelled = false
    httpJson<PortalUser>(`${CRM_API_BASE}/user/${selectedId}/`)
      .then((u) => {
        if (!cancelled) setResolvedSelectedName(getUserDisplayName(u))
      })
      .catch(() => {
        if (!cancelled) setResolvedSelectedName(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, users, allUsers])

  const handleSelect = (u: PortalUser | null) => {
    setSearchTerm("")
    setShowDropdown(false)
    if (!u) {
      onChange("all", null)
      return
    }
    onChange(u.id.toString(), getUserDisplayName(u))
  }

  const clearFilter = () => {
    setSearchTerm("")
    setShowDropdown(false)
    onChange("all", null)
  }

  const placeholderText = !isClientScoped
    ? "Select a client first"
    : hasNoUsersForClient
      ? "No users for client"
      : loading
        ? "Loading users..."
        : "All Users"

  const inputDisplayValue = searchTerm
    || (selectedId && !showDropdown ? (resolvedSelectedName ?? "") : "")
    || (!searchTerm && !selectedId && !showDropdown ? placeholderText : "")

  return (
    <div className={`relative ${containerWidth}`}>
      <Input
        placeholder={placeholderText}
        value={inputDisplayValue}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          setShowDropdown(true)
          if (selectedId) onChange("all", null)
        }}
        onFocus={() => {
          if (isEffectivelyDisabled) return
          setShowDropdown(true)
        }}
        onBlur={() => {
          setTimeout(() => setShowDropdown(false), 200)
        }}
        className={`${inputClass} ${isEffectivelyDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
        disabled={loading || isEffectivelyDisabled}
      />
      <button
        type="button"
        onClick={() => {
          if (isEffectivelyDisabled) return
          if (!showDropdown) {
            setShowDropdown(true)
            setSearchTerm("")
          } else {
            setShowDropdown(false)
          }
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-primary-foreground p-1 rounded hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed z-10`}
        disabled={loading || isEffectivelyDisabled}
      >
        <svg
          className={`${isCompact ? "w-3 h-3" : "w-4 h-4"} transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {(searchTerm || selectedId) && !isEffectivelyDisabled && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            clearFilter()
          }}
          className={`absolute ${isCompact ? "right-7" : "right-9"} top-1/2 -translate-y-1/2 text-gray-600 hover:text-primary-foreground hover:bg-primary rounded p-1 z-20`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {showDropdown && !isEffectivelyDisabled && (
        <div className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal" style={{ left: "0", width: "100%" }}>
          {loading ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Loading users...</div>
          ) : users.length === 0 ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>
              {searchTerm.trim() ? `No users found for "${searchTerm}"` : "No users available"}
            </div>
          ) : (
            <div>
              <div
                className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(null)}
              >
                All Users
              </div>
              {users.map((u) => (
                <div
                  key={u.id}
                  className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100 last:border-b-0`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(u)}
                >
                  {getUserDisplayName(u)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
